(function () {
  'use strict';

  if (window.__digitalFeeModuleInitialized) {
    return;
  }
  window.__digitalFeeModuleInitialized = true;

const STORE_ENABLED_SESSION_KEY_PREFIX = 'digitalFeeStoreEnabled:';

/** @type {boolean | null} */
let digitalFeeStoreEnabledCache = null;
/** @type {Promise<boolean> | null} */
let digitalFeeStoreEnabledPromise = null;

const ROW_SELECTOR = '.purchase-type, .accordion-variant-option';
const CONTAINER_SELECTOR = '.collapse-content, .format-tab-panel-content';

function getStoreEnabledSessionKey() {
  const storeNumber = window.digital_product_config?.store_number;
  return `${STORE_ENABLED_SESSION_KEY_PREFIX}${storeNumber || 'unknown'}`;
}

function readStoreEnabledSessionCache() {
  try {
    const raw = sessionStorage.getItem(getStoreEnabledSessionKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.enabled !== 'boolean') return null;
    return parsed.enabled;
  } catch {
    return null;
  }
}

function writeStoreEnabledSessionCache(enabled) {
  try {
    sessionStorage.setItem(
      getStoreEnabledSessionKey(),
      JSON.stringify({ enabled: Boolean(enabled), at: Date.now() })
    );
  } catch {
    /* private mode / quota */
  }
}

function rememberStoreEnabledFromPayload(data) {
  if (
    !Array.isArray(data) ||
    data.length === 0 ||
    !data.every((entry) => typeof entry?.storeEnabled === 'boolean')
  ) {
    return;
  }
  const enabled = data.some((entry) => entry.storeEnabled === true);
  digitalFeeStoreEnabledCache = enabled;
  writeStoreEnabledSessionCache(enabled);
}

/**
 * Store-level downloadprovidercode=* check. Failures are not a confirmed store-off.
 * @returns {Promise<boolean>}
 */
function isDigitalDeliveryNoticeStoreEnabled() {
  if (digitalFeeStoreEnabledCache !== null) {
    return Promise.resolve(digitalFeeStoreEnabledCache);
  }
  if (digitalFeeStoreEnabledPromise) {
    return digitalFeeStoreEnabledPromise;
  }

  const sessionCached = readStoreEnabledSessionCache();
  if (sessionCached !== null) {
    digitalFeeStoreEnabledCache = sessionCached;
    return Promise.resolve(sessionCached);
  }

  const config = window.digital_product_config;
  if (!config?.api_url || !config?.store_number) {
    digitalFeeStoreEnabledCache = false;
    return Promise.resolve(false);
  }

  const requestUrl = `${config.api_url}?storeNumber=${config.store_number}&downloadprovidercode=*`;
  digitalFeeStoreEnabledPromise = fetch(requestUrl)
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((data) => {
      const enabled =
        Array.isArray(data) &&
        data.some((entry) => entry?.storeEnabled === true);
      digitalFeeStoreEnabledCache = enabled;
      writeStoreEnabledSessionCache(enabled);
      return enabled;
    })
    .catch((e) => {
      console.error('[digital-fee] Store enabled check failed:', e);
      digitalFeeStoreEnabledCache = false;
      return false;
    })
    .finally(() => {
      digitalFeeStoreEnabledPromise = null;
    });

  return digitalFeeStoreEnabledPromise;
}

/**
 * ATC / cart: publisher isApplied is the source of truth.
 * storeEnabled=false must not skip this — TCU returns isApplied true with storeEnabled false.
 * @param {string} publisherCode
 * @returns {Promise<boolean>}
 */
async function shouldApplyDigitalFeeForPublisher(publisherCode) {
  const code = String(publisherCode || '').trim();
  if (!code) return false;

  try {
    const results = await queryDigitalDeliveryFeesApi(code);
    return Array.isArray(results) && results.some((entry) => entry?.isApplied === true);
  } catch (e) {
    console.error('[digital-fee] Publisher fee check failed:', e);
    return false;
  }
}

window.isDigitalDeliveryNoticeStoreEnabled = isDigitalDeliveryNoticeStoreEnabled;
window.shouldApplyDigitalFeeForPublisher = shouldApplyDigitalFeeForPublisher;
window.queryDigitalDeliveryFeesApi = queryDigitalDeliveryFeesApi;
window.buildDigitalFeeCartLine = buildDigitalFeeCartLine;
window.addCartItems = addCartItems;

/**
 * Shopify /cart/add.js often returns HTTP 200 with { status: 422 }.
 * @param {Response} response
 * @returns {Promise<any>}
 */
async function parseCartAddResponse(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || Number(payload?.status) >= 400) {
    throw new Error(
      payload?.description || payload?.message || `Failed to add items to cart (HTTP ${response.status})`
    );
  }
  return payload;
}

/**
 * @param {number|string} quantity
 * @returns {Promise<{id: number, quantity: number, properties: Record<string, string>} | null>}
 */
async function buildDigitalFeeCartLine(quantity) {
  const feeProductHandle = window.digital_product_config?.product_handle ?? '';
  if (!feeProductHandle) return null;

  try {
    const productResponse = await fetch(`/products/${feeProductHandle}.js`);
    if (!productResponse.ok) return null;

    const feeProduct = await productResponse.json();
    const feeVariantId = feeProduct.variants?.[0]?.id;
    if (!feeVariantId) return null;

    let metafields = [];
    if (typeof window.fetchMetafieldsOnce === 'function') {
      metafields = await window.fetchMetafieldsOnce(feeVariantId);
    }

    const getMetafieldValue = (key) => {
      const metafield = (metafields || []).find((m) => m.key === key);
      return metafield != null ? String(metafield.value ?? '') : '';
    };

    const feeSubClass = getMetafieldValue('sub_class');

    return {
      id: Number(feeVariantId),
      quantity: Number(quantity) || 1,
      properties: {
        _digital_delivery_fee: 'true',
        _is_digital: 'true',
        _fee_type: 'online_store',
        _department: getMetafieldValue('department'),
        _sub_department: getMetafieldValue('sub_department'),
        _class: getMetafieldValue('class'),
        _sub_class: feeSubClass,
      },
    };
  } catch (error) {
    console.error('[digital-fee] Failed to build fee line:', error);
    return null;
  }
}

/**
 * One /cart/add.js for product lines + digital fee. Does not add rental collateral.
 * @param {Array<{id: number|string, quantity: number|string, properties?: Record<string, string>}>} items
 * @returns {Promise<any>}
 */
async function addCartItems(items) {
  const payloadItems = (items || []).filter((item) => item?.id != null);
  if (payloadItems.length === 0) {
    throw new Error('No items to add to cart');
  }

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: payloadItems }),
  });
  return parseCartAddResponse(response);
}

/**
 * Checks if a digital fee should be applied for given publisher codes
 * @param {string | string[]} publisherCodes - Publisher code(s) to check
 * @returns {Promise<Array<{code: string, isApplied: boolean, storeEnabled?: boolean}>>}
 */
function queryDigitalDeliveryFeesApi(publisherCodes) {
  if (
    window.digital_product_config == undefined ||
    window.digital_product_config?.api_url == undefined ||
    window.digital_product_config?.store_number == undefined
  ) {
    throw Error("Digital fee config does not exist");
  }

  const api_url = window.digital_product_config.api_url;
  const store_number = window.digital_product_config.store_number;

  let uniqueCodes;
  if (typeof publisherCodes === "string") {
    uniqueCodes = [
      ...new Set(
        publisherCodes
          .split("|")
          .map((s) => s.trim())
          .filter(Boolean)
      ),
    ];
  } else {
    uniqueCodes = [
      ...new Set(
        publisherCodes.map((c) => String(c).trim()).filter(Boolean)
      ),
    ];
  }
  const queryValue = uniqueCodes.join("|");

  if (queryValue !== "") {
    const requestUrl = `${api_url}?storeNumber=${store_number}&downloadprovidercode=${queryValue}`;
    return fetch(requestUrl)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (!Array.isArray(data)) {
          throw new Error('Digital fee API returned a non-array response');
        }
        rememberStoreEnabledFromPayload(data);
        return data.map((entry) => ({
          code: entry.code,
          isApplied: entry.isApplied ?? false,
          storeEnabled: entry.storeEnabled ?? false,
        }));
      })
      .catch((e) => {
        console.error("[digital-fee] API request failed:", e);
        return [];
      });
  } else {
    return Promise.resolve([]);
  }
}

/** All `.purchase-checkbox` nodes whose `data-variant-id` matches (safe vs. CSS selector quirks). */
function cmrpFindPurchaseCheckboxesByVariantId(variantId) {
  const want = String(variantId || "");
  if (!want) return [];
  return Array.from(document.querySelectorAll("input.purchase-checkbox")).filter(
    (el) => el.getAttribute("data-variant-id") === want
  );
}

function announceFeaturedDigitalFee(el, label) {
  let live = el.querySelector('.purchase-type-digital-fee__announce');
  if (!live) {
    live = document.createElement('span');
    live.className = 'visually-hidden purchase-type-digital-fee__announce';
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    el.appendChild(live);
  }
  live.textContent = '';
  requestAnimationFrame(() => {
    if (live.isConnected) live.textContent = label;
  });
  window.setTimeout(() => {
    if (live.isConnected) live.remove();
  }, 1500);
}

function linkDigitalFeeDescribedBy(input, id, linked) {
  if (!input || !id) return;
  const ids = (input.getAttribute('aria-describedby') || '')
    .split(/\s+/)
    .filter((item) => item && item !== id);
  if (linked) ids.push(id);
  if (ids.length) input.setAttribute('aria-describedby', ids.join(' '));
  else input.removeAttribute('aria-describedby');
}

/** Short in-card label (e.g. "+ $5.99 digital delivery fee"). */
function digitalDeliveryFeeLabel() {
  const raw =
    document.querySelector('.digital-fee-amount')?.textContent?.trim() ||
    window.digital_product_config?.variant_price ||
    window.digital_product_config?.price ||
    '5.99';
  const amount = String(raw).replace(/^\$/, '').trim() || '5.99';
  return `+ $${amount} digital delivery fee`;
}

/**
 * @param {Element} row
 * @returns {HTMLElement}
 */
function ensureDigitalFeeStrip(row) {
  let el = row.querySelector('.purchase-type-digital-fee');
  if (!el) {
    el = document.createElement('div');
    el.className = 'purchase-type-digital-fee';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<div class="purchase-type-digital-fee__inner"><span class="purchase-type-digital-fee__text"></span></div>';
    row.appendChild(el);
  } else if (el.parentElement === row && row.lastElementChild !== el) {
    // Keep the fee strip last so it can pin to the card bottom.
    row.appendChild(el);
  }
  const text = el.querySelector('.purchase-type-digital-fee__text');
  if (text) text.textContent = digitalDeliveryFeeLabel();
  return el;
}

/**
 * Expand/collapse in-card fee strips for rows in a purchase grid container.
 * Prefer CMRP sync when available (also handles used-print notices).
 * @param {Element | null | undefined} container
 */
function syncDigitalFeeStripsInContainer(container) {
  if (!container) return;

  if (typeof window.syncCmrpVariantNoticeInCollapse === 'function') {
    window.syncCmrpVariantNoticeInCollapse(container);
    return;
  }

  const grid =
    container.querySelector('.purchase-types') ||
    container.querySelector('.accordion-variant-options') ||
    container;
  grid.querySelectorAll(ROW_SELECTOR).forEach((row) => {
    const checked = !!row.querySelector('.purchase-checkbox:checked');
    const hasDigitalFee = row.hasAttribute('data-digital-delivery-fee');
    if (hasDigitalFee) {
      // Expand/collapse is CSS-only via :has(.purchase-checkbox:checked).
      // JS only mounts the strip and keeps aria in sync.
      const el = ensureDigitalFeeStrip(row);
      const text = el.querySelector('.purchase-type-digital-fee__text');
      const input = row.querySelector(
        'input.purchase-checkbox, input.purchase-choice-checkbox'
      );
      const label = text?.textContent?.trim() || digitalDeliveryFeeLabel();
      if (text && text.textContent.trim() !== label) text.textContent = label;
      if (text && !text.id) {
        text.id = `digital-fee-label-${Math.random().toString(36).slice(2, 10)}`;
      }
      const wasFeatured = el.getAttribute('data-fee-featured') === 'true';
      if (text) text.removeAttribute('aria-hidden');
      el.removeAttribute('tabindex');
      el.removeAttribute('role');
      el.removeAttribute('aria-label');
      el.removeAttribute('aria-labelledby');
      if (checked) {
        el.removeAttribute('aria-hidden');
        el.setAttribute('data-fee-featured', 'true');
        if (text) {
          text.setAttribute('tabindex', '0');
          text.setAttribute('role', 'note');
        }
        if (text && input) linkDigitalFeeDescribedBy(input, text.id, true);
        if (text && !wasFeatured) announceFeaturedDigitalFee(el, label);
      } else {
        el.setAttribute('aria-hidden', 'true');
        el.removeAttribute('data-fee-featured');
        if (text) {
          text.removeAttribute('tabindex');
          text.removeAttribute('role');
        }
        el.querySelector('.purchase-type-digital-fee__announce')?.remove();
        if (text && input) linkDigitalFeeDescribedBy(input, text.id, false);
      }
    } else {
      row.querySelector('.purchase-type-digital-fee')?.remove();
    }
  });
}

function syncAllDigitalFeeStrips() {
  document.querySelectorAll(CONTAINER_SELECTOR).forEach((c) => {
    syncDigitalFeeStripsInContainer(c);
  });
}

window.syncDigitalFeeStripsInContainer = syncDigitalFeeStripsInContainer;
window.syncAllDigitalFeeStrips = syncAllDigitalFeeStrips;

/**
 * Show/hide `Digital delivery fees may apply.` above digital variant grids.
 * Creates the notice on PDP digital panels when missing.
 * @param {boolean} storeEnabled
 */
function syncDigitalDeliveryNoticeElements(storeEnabled) {
  if (storeEnabled) {
    document
      .querySelectorAll(
        '.format-tab-panel[data-format="digital"] .format-tab-panel-content'
      )
      .forEach((content) => {
        let notice = content.querySelector(':scope > .digital-delivery-notice');
        if (!notice) {
          notice = document.createElement('div');
          notice.className = 'digital-delivery-notice';
          notice.textContent = 'Digital delivery fees may apply.';
          const grid = content.querySelector(
            '.accordion-variant-options, .purchase-types'
          );
          if (grid) content.insertBefore(notice, grid);
          else content.prepend(notice);
        }
      });
  }

  document.querySelectorAll('.digital-delivery-notice').forEach((el) => {
    if (storeEnabled) {
      el.removeAttribute('hidden');
      el.removeAttribute('aria-hidden');
    } else {
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
    }
  });
}

/**
 * Clear digital fee markers / in-card strips and remove digital fee tooltips on digital rows only.
 * Print used-material tooltips use `.notice-tooltip` without being fee-specific; only remove
 * tooltips that were the old digital-fee affordance (sibling to digital labels / data-variant-id
 * on digital rows).
 */
function suppressDigitalFeeNoticesOnDigitalVariants() {
  const collapsesToSync = new Set();

  document
    .querySelectorAll('input.purchase-checkbox[data-is-digital="true"]')
    .forEach((input) => {
      const row = input.closest(ROW_SELECTOR);
      if (!row) return;

      row.removeAttribute("data-digital-delivery-fee");
      row.removeAttribute("data-variant-notice");
      row.querySelector(".purchase-type-digital-fee")?.remove();
      // Legacy digital-fee tooltips (replaced by in-card strip + delivery notice).
      row.querySelectorAll(".notice-tooltip[data-variant-id]").forEach((el) => {
        el.remove();
      });

      const collapse = row.closest(CONTAINER_SELECTOR);
      if (collapse) collapsesToSync.add(collapse);
    });

  syncDigitalDeliveryNoticeElements(false);

  collapsesToSync.forEach((c) => syncDigitalFeeStripsInContainer(c));
}

// Digital delivery fee API: mark matching digital rows for in-card fee strip (CMRP + PDP).
// Multiple variants can share one publisher code (e.g. unified-view merged digitals) — map code → many variant ids.
function pageHasDigitalPurchaseUi() {
  return Boolean(
    document.querySelector('input.purchase-checkbox[data-is-digital="true"]') ||
      document.querySelector('.textbook-variant-picker') ||
      document.body.classList.contains('course-materials-page')
  );
}

async function applyDigitalDeliveryFeeVariantNotices() {
  if (!pageHasDigitalPurchaseUi()) {
    return;
  }

  syncDigitalDeliveryNoticeElements(true);

  const checkboxContainers = document.querySelectorAll(
    ".purchase-checkbox-container"
  );

  if (checkboxContainers.length === 0) {
    return;
  }

  /** @type {Map<string, Set<string>>} */
  const publisherToVariantIds = new Map();
  checkboxContainers.forEach((container) => {
    const input = container.querySelector("input.purchase-checkbox");

    if (!input) return;

    const isDigital = input.dataset.isDigital === "true";
    const publisherCode = input.dataset.publisherCode;
    const variantId = input.getAttribute("data-variant-id");

    if (
      isDigital &&
      publisherCode &&
      publisherCode !== "null" &&
      publisherCode.trim() !== ""
    ) {
      const code = publisherCode.trim();
      if (!publisherToVariantIds.has(code)) {
        publisherToVariantIds.set(code, new Set());
      }
      if (variantId) {
        publisherToVariantIds.get(code).add(variantId);
      }
    }
  });

  const publisherCodesToCheck = Array.from(publisherToVariantIds.keys());
  if (publisherCodesToCheck.length === 0) {
    return;
  }

  return queryDigitalDeliveryFeesApi(publisherCodesToCheck).then((results) => {
    if (!Array.isArray(results)) {
      return;
    }

    const collapsesToSync = new Set();
    /** @type {Set<string>} */
    const appliedVariantIds = new Set();

    results.forEach((result) => {
      const isApplied = result.isApplied ?? false;
      if (!isApplied) return;

      const variantIds = publisherToVariantIds.get(result.code);
      if (!variantIds || variantIds.size === 0) return;

      variantIds.forEach((vid) => {
        appliedVariantIds.add(vid);
        cmrpFindPurchaseCheckboxesByVariantId(vid).forEach((checkbox) => {
          const row = checkbox.closest(ROW_SELECTOR);
          if (row) {
            row.setAttribute("data-digital-delivery-fee", "true");
            row.removeAttribute("data-variant-notice");
            // In-card strip replaces digital-fee tooltips.
            row.querySelectorAll(".notice-tooltip[data-variant-id]").forEach((el) => {
              el.remove();
            });
            const collapse = row.closest(CONTAINER_SELECTOR);
            if (collapse) collapsesToSync.add(collapse);
          }
        });
      });
    });

    // Clear fee marker on digital rows that are no longer applied.
    document
      .querySelectorAll(
        ".purchase-type[data-digital-delivery-fee], .accordion-variant-option[data-digital-delivery-fee]"
      )
      .forEach((row) => {
        const input = row.querySelector("input.purchase-checkbox");
        const vid = input?.getAttribute("data-variant-id");
        if (vid && !appliedVariantIds.has(vid)) {
          row.removeAttribute("data-digital-delivery-fee");
          row.querySelector(".purchase-type-digital-fee")?.remove();
          const collapse = row.closest(CONTAINER_SELECTOR);
          if (collapse) collapsesToSync.add(collapse);
        }
      });

    if (collapsesToSync.size > 0) {
      collapsesToSync.forEach((c) => syncDigitalFeeStripsInContainer(c));
    }
    // Always re-sync all containers so used-print suppression of the
    // delivery notice is restored after a global show/hide pass.
    syncAllDigitalFeeStrips();
    window.courseMaterialsPage?.components?.interactionManager?._refreshChoiceSelectedSummaries?.();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", applyDigitalDeliveryFeeVariantNotices);
} else {
  applyDigitalDeliveryFeeVariantNotices();
}

window.applyDigitalDeliveryFeeVariantNotices =
  applyDigitalDeliveryFeeVariantNotices;

document.addEventListener("product-display:updated", () => {
  applyDigitalDeliveryFeeVariantNotices();
});

document.addEventListener(
  "change",
  (e) => {
    const checkbox = e.target;
    if (!checkbox || !checkbox.classList?.contains("purchase-checkbox")) return;
    const container = checkbox.closest(CONTAINER_SELECTOR);
    if (container) syncDigitalFeeStripsInContainer(container);
  },
  true
);

})();
